/**
 * getRemovedEvents
 *
 * Returns an array of events that were removed from the timeline.
 *
 * @param previousSelection
 * @param currentSelection
 * @returns {*}
 */
export const getRemovedEvents = (previousSelection, currentSelection) => {
  return previousSelection.filter((eventCode) => {
    return !currentSelection.includes(eventCode);
  });
};

/**
 * getAddedEvents
 *
 * Returns an array of events that were added to the timeline.
 *
 * @param previousSelection
 * @param currentSelection
 * @returns {*}
 */
export const getAddedEvents = (previousSelection, currentSelection) => {
  return currentSelection.filter((eventCode) => {
    return !previousSelection.includes(eventCode);
  });
};

/**
 * addUniqueEvents
 *
 * Adds unique events to the target array.
 *
 * @param targetEventCodes
 * @param newEventCodes
 */
export const addUniqueEvents = (targetEventCodes, newEventCodes) => {
  newEventCodes.forEach((eventCode) => {
    if (!targetEventCodes.includes(eventCode)) {
      targetEventCodes.push(eventCode);
    }
  });
};

/**
 * handleMatchedEvents
 *
 * Handles matched events by updating the removed events map.
 *
 * @param removedEventsMap
 * @param date
 * @param eventCodesForDate
 * @param eventCodesToRemove
 */
export const handleMatchedEvents = (removedEventsMap, date, eventCodesForDate, eventCodesToRemove) => {
  const matchedEventCodes = eventCodesForDate.filter((eventCode) => {
    return eventCodesToRemove.includes(eventCode);
  });
  if (matchedEventCodes.length === 0) return;
  if (!removedEventsMap[date]) {
    removedEventsMap[date] = [...matchedEventCodes];
  } else {
    addUniqueEvents(removedEventsMap[date], matchedEventCodes);
  }
};

/**
 * addFilteredEvents
 *
 * Filters out removed events from the events array and adds them to the accumulator if any remain.
 *
 * @param filteredTimelines
 * @param date
 * @param eventCodesForDate
 * @param eventCodesToRemove
 * @returns {*}
 */
export const addFilteredEvents = (filteredTimelines, date, eventCodesForDate, eventCodesToRemove) => {
  const filteredEventCodes = eventCodesForDate.filter((eventCode) => {
    return !eventCodesToRemove.includes(eventCode);
  });
  if (filteredEventCodes.length > 0) {
    filteredTimelines[date] = filteredEventCodes;
  }
  return filteredTimelines;
};

/**
 * ensureDateInTimelines
 *
 * Ensures that a date exists in the timelines object, initializing it with an empty array if not present.
 *
 * @param timelines
 * @param date
 */
export const ensureDateInTimelines = (timelines, date) => {
  if (!timelines[date]) timelines[date] = [];
};

/**
 * addEventIfNotPresent
 *
 * Adds an event to the events array if it is not already present.
 *
 * @param eventCodes
 * @param eventCode
 */
export const addEventIfNotPresent = (eventCodes, eventCode) => {
  if (!eventCodes.includes(eventCode)) eventCodes.push(eventCode);
};

/**
 * removeEventFromRemovedEvents
 *
 * Removes an event from the removed events map for a specific date.
 *
 * @param removedEventsMap
 * @param date
 * @param eventCode
 */
export const removeEventFromRemovedEvents = (removedEventsMap, date, eventCode) => {
  removedEventsMap[date] = removedEventsMap[date].filter((code) => {
    return code !== eventCode;
  });
  if (removedEventsMap[date].length === 0) delete removedEventsMap[date];
};

/**
 * sortTimelinesByDateDesc
 *
 * Sorts the timelines object by date in descending order.
 *
 * @param timelines
 * @returns {*}
 */
export const sortTimelinesByDateDesc = (timelines) => {
  return Object.entries(timelines)
    .sort(([dateA], [dateB]) => {
      return new Date(dateB) - new Date(dateA);
    })
    .reduce((sortedTimelines, [date, eventCodes]) => {
      sortedTimelines[date] = eventCodes;
      return sortedTimelines;
    }, {});
};

/**
 * updateTimelinesAndRemovedEvents
 *
 * Updates the timelines and removed events based on added events.
 *
 * @param addedEventCodes
 * @param previousRemovedEventsMap
 * @param previousFilteredTimelinesObj
 * @returns {{sortedTimelines: *, updatedRemovedEvents}}
 */
export const updateTimelinesAndRemovedEvents = (
  addedEventCodes,
  previousRemovedEventsMap,
  previousFilteredTimelinesObj
) => {
  const updatedRemovedEventsMap = { ...previousRemovedEventsMap };
  const updatedTimelines = { ...previousFilteredTimelinesObj.timelines };

  Object.entries(previousRemovedEventsMap).forEach(([date, removedEventCodes]) => {
    removedEventCodes.forEach((eventCode) => {
      if (!addedEventCodes.includes(eventCode)) return;
      ensureDateInTimelines(updatedTimelines, date);
      addEventIfNotPresent(updatedTimelines[date], eventCode);
      removeEventFromRemovedEvents(updatedRemovedEventsMap, date, eventCode);
    });
  });

  const sortedTimelines = sortTimelinesByDateDesc(updatedTimelines);
  return { sortedTimelines, updatedRemovedEvents: updatedRemovedEventsMap };
};
